import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { C } from '../../../theme';

// ============================================================
// MESSAGE BUBBLE COMPONENTS
// Pure UI — no hooks, no API calls, no side effects.
// ============================================================

export function SystemMessage({ text }) {
  return (
    <Box sx={{ my: 2, px: 3 }}>
      <Typography variant="caption" sx={{
        color: C.textDisabled,
        display: 'block',
        fontSize: '0.72rem',
        lineHeight: 1.7,
        fontStyle: 'italic',
        whiteSpace: 'pre-line'
      }}>{text}</Typography>
    </Box>
  );
}

export function UserBubble({ text, isMe, senderAlias }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: C.textMuted, mb: 0.3, px: 0.5 }}>
        {isMe ? 'You' : senderAlias}
      </Typography>
      <Box sx={{
        bgcolor: isMe ? C.teal : '#0D2E30',
        color: '#EFEFEF',
        px: 2, py: 1,
        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        border: isMe ? 'none' : '1px solid ' + C.teal,
        maxWidth: '75%',
        wordBreak: 'break-word'
      }}>
        <Typography variant="body2" sx={{ color: '#EFEFEF' }}>{text}</Typography>
      </Box>
    </Box>
  );
}

export function QABlock({ questionText, answerText, isMe, senderAlias }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: C.textMuted, mb: 0.3, px: 0.5, fontStyle: 'italic', maxWidth: '75%' }}>
        Re: "{questionText}"
      </Typography>
      <Typography variant="caption" sx={{ color: C.textMuted, mb: 0.3, px: 0.5 }}>
        {isMe ? 'You' : senderAlias}
      </Typography>
      <Box sx={{
        bgcolor: isMe ? C.teal : '#0D2E30',
        color: '#EFEFEF',
        px: 2, py: 1,
        borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        border: isMe ? 'none' : '1px solid ' + C.teal,
        maxWidth: '75%',
        wordBreak: 'break-word'
      }}>
        <Typography variant="body2" sx={{ color: '#EFEFEF' }}>{answerText}</Typography>
      </Box>
    </Box>
  );
}

export function QuestionChip({ text, isMe, isAnswered, color = 'default' }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: C.textMuted, mb: 0.3, px: 0.5 }}>
        {isMe ? 'You asked' : 'They asked'}
      </Typography>
      <Chip label={text} variant="outlined"
        sx={{
          maxWidth: '80%', height: 'auto',
          borderColor: color === 'success' ? C.teal : isMe ? C.yellow : C.teal,
          color: color === 'success' ? C.teal : isMe ? C.yellow : C.teal,
          '& .MuiChip-label': { whiteSpace: 'normal', py: 0.75, px: 1.5 }
        }} />
      {!isAnswered && isMe && (
        <Typography variant="caption" sx={{ color: C.textDisabled, mt: 0.3, px: 0.5 }}>
          Waiting for their answer...
        </Typography>
      )}
    </Box>
  );
}

export function BucketDisplay({ myRemaining, otherRemaining, phaseLabel, otherAlias }) {
  if (!phaseLabel) return null;
  const myColor = myRemaining <= 1 ? C.orange : myRemaining <= 3 ? C.yellow : C.teal;
  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mb: 1, px: 2 }}>
      <Chip
        label={`You: ${myRemaining} ${phaseLabel} msg${myRemaining !== 1 ? 's' : ''} left`}
        size="small"
        sx={{ bgcolor: myColor, color: myRemaining <= 3 ? C.yellowText : '#EFEFEF', fontWeight: 'bold', fontSize: '0.7rem' }}
      />
      <Chip
        label={`${otherAlias || 'Them'}: ${otherRemaining} left`}
        size="small"
        variant="outlined"
        sx={{ fontSize: '0.7rem', color: C.textMuted, borderColor: C.border }}
      />
    </Box>
  );
}
